import {
  programKindLabels,
  recurrenceModelLabels,
  type ProgramDetail,
  type ProgramKind,
  type RecurrenceModel,
} from '@/entities/training/model/librarySchema';

const UNPLANNED_TRIGGER_LABELS: Record<string, string> = {
  regulation_changed: 'изменение НПА',
  incident: 'несчастный случай',
  transfer: 'перевод',
  inspection_order: 'предписание ГИТ',
  knowledge_gap: 'выявленный пробел знаний',
};

function kindTitle(program: ProgramDetail): string {
  if (!program.program_kind) {
    return 'не указан в каталоге';
  }
  return programKindLabels[program.program_kind as ProgramKind] ?? program.program_kind;
}

const KIND_DESCRIPTION: Record<string, string> = {
  briefing_induction:
    'Вводный инструктаж проводится при приёме на работу (переводе) до начала трудовой деятельности. ' +
    'Программа знакомит работника с условиями труда на предприятии, основными опасными и вредными факторами производства, ' +
    'правилами внутреннего трудового распорядка и действиями при возникновении аварийных ситуаций. ' +
    'Проводит специалист по ОТ или уполномоченное лицо.',
  briefing_primary_workplace:
    'Первичный инструктаж на рабочем месте проводится до начала самостоятельной работы. ' +
    'Работник изучает конкретные опасности на своём рабочем месте, безопасные приёмы и методы выполнения работ, ' +
    'порядок подготовки к работе и применение средств индивидуальной защиты. ' +
    'Проводит непосредственный руководитель работ.',
  briefing_repeated:
    'Повторный инструктаж проводится для всех работников не реже одного раза в шесть месяцев ' +
    '(для работ с повышенной опасностью — не реже одного раза в три месяца). ' +
    'Цель — закрепить и проверить знания требований охраны труда, освежить навыки безопасного поведения на рабочем месте. ' +
    'Проводится по программе первичного инструктажа в полном объёме.',
  briefing_unscheduled:
    'Внеплановый инструктаж проводится при изменении технологического процесса, замене оборудования, ' +
    'нарушении работниками требований ОТ, перерывах в работе, а также по требованию надзорных органов. ' +
    'Объём и содержание определяются причиной проведения.',
  briefing_targeted:
    'Целевой инструктаж проводится при выполнении разовых работ, не связанных с прямыми обязанностями по специальности, ' +
    'при ликвидации последствий аварий, а также при работах по наряду-допуску или распоряжению. ' +
    'Содержание инструктажа определяется характером предстоящих работ.',
  training:
    'Программа обучения по охране труда: теоретическая подготовка и практические занятия ' +
    'с последующей проверкой знаний в установленном порядке. Результат — протокол комиссии и допуск к работе.',
  knowledge_check:
    'Проверка знаний требований охраны труда проводится комиссией работодателя или в аккредитованной организации. ' +
    'По результатам оформляется протокол и (при положительном результате) удостоверение установленного образца.',
  internship_program:
    'Стажировка на рабочем месте — практическое обучение безопасным методам и приёмам выполнения работ ' +
    'под руководством опытного работника (наставника). Завершается проверкой приобретённых навыков ' +
    'и оформлением допуска к самостоятельной работе.',
};

export function buildTrainingKindMarkdown(program: ProgramDetail): string {
  const kind = kindTitle(program);
  const kindDesc = KIND_DESCRIPTION[program.program_kind ?? ''] ?? '';

  const lines = [`Программа относится к виду **«${kind}»**.`];
  if (kindDesc) {
    lines.push('', kindDesc);
  }

  return lines.join('\n');
}

function mechanicsNarrative(program: ProgramDetail): string {
  const pr = program.passing_rules;
  if (!pr) {
    return 'Правила прохождения в карточке программы не заполнены — уточните детали у ответственного за обучение.';
  }
  if (pr.is_level_based) {
    return (
      'Обучение **уровневое** — сотрудник проходит подготовку по группе/категории, ' +
      'соответствующей его должности и функционалу согласно матрице обучения. ' +
      'Сроки действия и периодичность переподготовки могут различаться по уровням (см. «Уровни / группы»).'
    );
  }
  if (pr.recurrence_model === 'cyclic') {
    return (
      'Обучение **циклическое** — после успешного прохождения результат действует ограниченный срок. ' +
      'По истечении срока действия необходимо пройти повторное обучение и проверку знаний.'
    );
  }
  return (
    'Обучение **разовое** — проводится однократно (при приёме, переводе или по специальному основанию). ' +
    'Результат фиксируется в карточке сотрудника. Повторное прохождение — только по отдельному назначению.'
  );
}

export function buildMechanicsMarkdown(program: ProgramDetail): string {
  const pr = program.passing_rules;
  const narrative = mechanicsNarrative(program);
  const lines: string[] = [narrative, ''];

  if (pr) {
    const model = recurrenceModelLabels[pr.recurrence_model as RecurrenceModel] ?? pr.recurrence_model;
    lines.push(`- **Модель:** ${model}`);
    if (pr.period_months != null) {
      lines.push(`- **Периодичность:** каждые ${pr.period_months} мес.`);
    }
    if (pr.validity_months != null) {
      lines.push(`- **Срок действия:** ${pr.validity_months} мес.`);
    }
    if (pr.min_retry_interval_days != null && pr.min_retry_interval_days > 0) {
      lines.push(`- **Минимальный интервал между попытками:** ${pr.min_retry_interval_days} дн.`);
    }
    if (pr.max_attempts_per_period != null) {
      lines.push(`- **Максимум попыток за период:** ${pr.max_attempts_per_period}`);
    }
    if (pr.unplanned_triggers?.length) {
      const labels = pr.unplanned_triggers.map((t) => UNPLANNED_TRIGGER_LABELS[t] ?? t).join(', ');
      lines.push(`- **Основания для внепланового назначения:** ${labels}`);
    }
  }

  return lines.join('\n');
}

export function buildExternalTrainingMarkdown(program: ProgramDetail): string {
  if (program.is_external) {
    return [
      'Обучение проводится **внешней аккредитованной организацией**.',
      '',
      '- По итогам оформляется удостоверение, протокол проверки знаний или диплом.',
      '- Скан-копия подтверждающего документа вносится в карточку сотрудника.',
      '- Сроки и формат согласуются со службой ОТ и кадрами.',
    ].join('\n');
  }

  return [
    'Обучение проводится **внутри организации** — силами штатных специалистов, комиссии по проверке знаний ' +
      'или в учебном классе предприятия.',
    '',
    '- Расписание и материалы формируются ответственным за обучение.',
    '- Регистрация прохождения — в журнале (для инструктажей) или протоколе комиссии (для обучения).',
  ].join('\n');
}

function rulesSummaryLines(program: ProgramDetail): string[] {
  const pr = program.passing_rules;
  if (!pr) {
    return ['Сроки и формат определяются локальным положением об обучении по охране труда.'];
  }
  const parts: string[] = [];
  if (pr.recurrence_model === 'cyclic' && pr.period_months != null) {
    parts.push(`повторять не реже чем раз в **${pr.period_months}** мес.`);
  }
  if (pr.validity_months != null) {
    parts.push(`подтверждать допуск в срок (действие **${pr.validity_months}** мес.)`);
  }
  if (pr.max_attempts_per_period != null) {
    parts.push(`не более **${pr.max_attempts_per_period}** попыток за период`);
  }
  if (parts.length === 0) {
    return ['Следовать регламенту комиссии и инструкциям курса.'];
  }
  return [`Требования: ${parts.join('; ')}.`];
}

const KIND_HOW_TO_PASS: Record<string, string[]> = {
  briefing_induction: [
    '- Прослушать инструктаж специалиста по охране труда.',
    '- Ответить на контрольные вопросы (устно или в журнале).',
    '- Поставить подпись в журнале регистрации вводного инструктажа.',
  ],
  briefing_primary_workplace: [
    '- Пройти инструктаж у непосредственного руководителя на рабочем месте.',
    '- Продемонстрировать знание безопасных приёмов работы.',
    '- Расписаться в журнале инструктажей на рабочем месте.',
  ],
  briefing_repeated: [
    '- Пройти повторный инструктаж у руководителя работ.',
    '- Ответить на контрольные вопросы по программе первичного инструктажа.',
    '- Расписаться в журнале.',
  ],
  briefing_unscheduled: [
    '- Пройти инструктаж в объёме, определённом причиной проведения.',
    '- Ответить на контрольные вопросы по теме инструктажа.',
    '- Расписаться в журнале.',
  ],
  briefing_targeted: [
    '- Изучить содержание наряда-допуска / распоряжения на работу.',
    '- Пройти целевой инструктаж у ответственного руководителя работ.',
    '- Расписаться в наряде-допуске или журнале регистрации.',
  ],
  training: [
    '- Пройти теоретическую подготовку (лекции, самоподготовка по материалам).',
    '- При необходимости — выполнить практическую часть (тренировка, стажировка).',
    '- Сдать проверку знаний — тест или устный экзамен перед комиссией.',
    '- Получить протокол и (при необходимости) удостоверение.',
  ],
  knowledge_check: [
    '- Подготовиться по утверждённым вопросам и билетам.',
    '- Явиться на заседание комиссии по проверке знаний в назначенную дату.',
    '- Успешно ответить на вопросы комиссии / пройти тестирование.',
  ],
  internship_program: [
    '- Пройти стажировку под руководством наставника (от 2 до 14 смен).',
    '- Освоить безопасные приёмы выполнения работ на своём рабочем месте.',
    '- Сдать проверку навыков руководителю и получить допуск к самостоятельной работе.',
  ],
};

export function buildHowToPassMarkdown(program: ProgramDetail): string {
  const bullets = KIND_HOW_TO_PASS[program.program_kind ?? ''] ?? KIND_HOW_TO_PASS['training'] ?? [];

  return [...rulesSummaryLines(program), '', ...bullets].join('\n');
}

const KIND_HOW_TO_LEARN: Record<string, string[]> = {
  briefing_induction: [
    '- Прибыть в кабинет охраны труда в день, указанный в направлении.',
    '- Внимательно прослушать программу инструктажа, задать вопросы.',
    '- При необходимости ознакомиться с дополнительными материалами (стенды, памятки).',
  ],
  briefing_primary_workplace: [
    '- Ознакомиться с инструкциями по охране труда для вашей профессии / вида работ.',
    '- Изучить рабочее место: расположение оборудования, аварийные выходы, средства защиты.',
    '- Задать руководителю работ все возникшие вопросы до начала самостоятельной работы.',
  ],
  briefing_repeated: [
    '- Перечитать инструкции по охране труда по вашей должности / виду работ.',
    '- Обратить внимание на изменения в технологии, оборудовании, нормативных требованиях.',
    '- Подготовить вопросы для обсуждения с руководителем работ.',
  ],
  briefing_unscheduled: [
    '- Ознакомиться с причиной проведения инструктажа (приказ, предписание, изменение).',
    '- Изучить новые / изменённые требования и инструкции.',
    '- Обсудить практические действия с руководителем работ.',
  ],
  briefing_targeted: [
    '- Изучить содержание наряда-допуска и перечень мероприятий по безопасности.',
    '- Ознакомиться с характером предстоящих работ и возможными опасностями.',
  ],
  training: [
    '- Откройте назначение в разделе **«Моё обучение»** и проверьте сроки.',
    '- Пройдите теоретические материалы (очно или дистанционно — по регламенту подразделения).',
    '- При наличии практической части — согласуйте график занятий с руководителем.',
  ],
  knowledge_check: [
    '- Получите перечень вопросов / билетов у ответственного за обучение.',
    '- Изучите нормативные документы и инструкции, указанные в программе.',
    '- Пройдите самопроверку перед заседанием комиссии.',
  ],
  internship_program: [
    '- Согласуйте с наставником график и программу стажировки.',
    '- Ведите записи о выполненных работах и освоенных навыках.',
    '- Задавайте вопросы наставнику — стажировка на это и рассчитана.',
  ],
};

export function buildHowToLearnMarkdown(program: ProgramDetail): string {
  const bullets = KIND_HOW_TO_LEARN[program.program_kind ?? ''] ?? KIND_HOW_TO_LEARN['training'] ?? [];

  const lines = [...bullets];

  if (program.levels.length > 0) {
    lines.push(
      '',
      `Программа имеет уровни: **${program.levels.map((l) => l.level_name).join('**, **')}** — ` +
        'обучение проводится по уровню, закреплённому в матрице для вашей должности.',
    );
  }

  lines.push('', 'При переносе даты — согласуйте с непосредственным руководителем.');

  return lines.join('\n');
}

const KIND_REQUIRED_INFO: Record<string, string[]> = {
  briefing_induction: ['- Документ, удостоверяющий личность.', '- Трудовой договор / направление от кадров.'],
  briefing_primary_workplace: [
    '- Запись о прохождении вводного инструктажа.',
    '- Спецодежда и СИЗ (при наличии — по нормам выдачи).',
  ],
  briefing_repeated: ['- Инструкции по охране труда по вашей профессии (для повторного изучения).'],
  briefing_unscheduled: [
    '- Приказ / предписание, послужившие основанием для инструктажа.',
    '- Обновлённые инструкции (при изменении НПА / технологии).',
  ],
  briefing_targeted: [
    '- Наряд-допуск или распоряжение на выполнение работ.',
    '- СИЗ, соответствующие характеру работ.',
  ],
  training: [
    '- Табельный номер и должность (для протоколов и приказов).',
    '- Медицинский допуск, если по программе требуется заключение врача.',
    '- Действующие удостоверения и допуски (электробезопасность, высота и т.д.) — при наличии.',
  ],
  knowledge_check: [
    '- Табельный номер, должность и подразделение.',
    '- Действующие удостоверения (при повторной проверке).',
    '- Журнал учёта (заполняет секретарь комиссии).',
  ],
  internship_program: [
    '- Запись о прохождении вводного и первичного инструктажей.',
    '- Медицинский допуск к работе (при необходимости).',
    '- Спецодежда и СИЗ.',
  ],
};

export function buildRequiredInfoMarkdown(program: ProgramDetail): string {
  const bullets = KIND_REQUIRED_INFO[program.program_kind ?? ''] ?? KIND_REQUIRED_INFO['training'] ?? [];

  const lines = ['Перед прохождением подготовьте:', '', ...bullets];

  if (program.is_external) {
    lines.push('', 'Для внешнего обучения дополнительно: реквизиты договора с провайдером и контакт куратора.');
  }

  if (program.passing_rules?.unplanned_triggers?.length) {
    lines.push(
      '',
      'При внеплановом назначении сохраните основание (приказ, предписание, акт) — его могут запросить при аудите.',
    );
  }

  return lines.join('\n');
}
